<template>
  <div>
    <!-- Header bar -->
    <div class="d-flex align-center px-2 py-2">
      <v-icon class="mr-2">mdi-calculator</v-icon>
      <span class="text-subtitle-1 font-weight-medium">Billing Entry</span>
      <v-spacer />
      <!-- Step indicator -->
      <span class="text-caption grey--text mr-4">
        Step {{ currentStep }} of 2 —
        {{ currentStep === 1 ? 'Period &amp; Data' : 'Review &amp; Save' }}
      </span>
      <v-btn text x-small @click="resetForm">
        <v-icon x-small left>mdi-refresh</v-icon>Reset
      </v-btn>
    </div>

    <v-divider />

    <!-- ── STEP 1 ─────────────────────────────────────────────────────── -->
    <div v-show="currentStep === 1">
      <v-form ref="step1Form" class="pa-4">

        <!-- ① Billing period -->
        <div class="field-section-label">Billing Period</div>
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-menu v-model="startDateMenu" :close-on-content-click="false"
              transition="scale-transition" offset-y min-width="auto">
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="formattedStartDate"
                  label="Start Date"
                  prepend-inner-icon="mdi-calendar"
                  readonly dense outlined hide-details
                  v-bind="attrs" v-on="on"
                />
              </template>
              <v-date-picker v-model="period.startDate" @input="startDateMenu = false" />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6">
            <v-menu v-model="endDateMenu" :close-on-content-click="false"
              transition="scale-transition" offset-y min-width="auto">
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="formattedEndDate"
                  label="End Date"
                  prepend-inner-icon="mdi-calendar"
                  readonly dense outlined hide-details
                  v-bind="attrs" v-on="on"
                />
              </template>
              <v-date-picker v-model="period.endDate" @input="endDateMenu = false" />
            </v-menu>
          </v-col>
        </v-row>

        <!-- ② Data source -->
        <div class="field-section-label mt-4">Consumption Source</div>
        <v-radio-group v-model="dataEntryMode" row hide-details
          class="mt-0 mb-2" @change="onDataEntryModeChange">
          <v-radio label="Automatic" value="automatic"
            :disabled="!period.startDate || !period.endDate" />
          <v-radio label="Manual" value="manual" />
        </v-radio-group>

        <!-- Automatic: fetch button -->
        <template v-if="dataEntryMode === 'automatic'">
          <v-btn small color="primary" class="mb-2"
            :disabled="!period.startDate || !period.endDate || loadingConsumption"
            :loading="loadingConsumption"
            @click="fetchConsumptionData">
            <v-icon left small>mdi-download</v-icon>
            Fetch Consumption Data
          </v-btn>
        </template>

        <!-- Manual standard: initial + final readings -->
        <template v-if="dataEntryMode === 'manual' && !isTimeOfUseMode">
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model.number="manualReading.initial"
                label="Initial Reading (kWh)"
                type="number" step="0.1"
                dense outlined hide-details
                @input="syncManualConsumption"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model.number="manualReading.final"
                label="Final Reading (kWh)"
                type="number" step="0.1"
                dense outlined hide-details
                @input="syncManualConsumption"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                :value="calculatedConsumption"
                label="Total (kWh)"
                readonly dense outlined hide-details
                :color="calculatedConsumption > 0 ? 'success' : ''"
              />
            </v-col>
          </v-row>
          <div class="d-flex justify-end mt-2">
            <v-btn small color="primary"
              :disabled="calculatedConsumption === 0 || !period.startDate || !period.endDate"
              @click="confirmManualData">
              <v-icon left small>mdi-check</v-icon>Confirm
            </v-btn>
          </div>
        </template>

        <!-- Manual TOU: readings table -->
        <template v-if="dataEntryMode === 'manual' && isTimeOfUseMode">
          <v-simple-table dense class="tou-table mb-2">
            <template #default>
              <thead>
                <tr>
                  <th>Period</th><th>Initial</th><th>Final</th><th class="text-right">Units</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line in touUnitLines" :key="line.key">
                  <td class="text-caption font-weight-medium">{{ line.label }}</td>
                  <td>
                    <v-text-field
                      v-model.number="touReadings[line.key].initial"
                      type="number" step="0.01"
                      dense outlined hide-details class="tou-input"
                      @input="syncTouLine(line.key)"
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model.number="touReadings[line.key].final"
                      type="number" step="0.01"
                      dense outlined hide-details class="tou-input"
                      @input="syncTouLine(line.key)"
                    />
                  </td>
                  <td class="text-right text-caption font-weight-bold">
                    {{ formatTouUnits(line.key) }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-caption grey--text">
              Total: <strong>{{ formattedTouTotal }} kWh</strong>
            </span>
            <v-btn small color="primary"
              :disabled="touTotalUnits === 0 || !period.startDate || !period.endDate"
              @click="confirmManualData">
              <v-icon left small>mdi-check</v-icon>Confirm
            </v-btn>
          </div>
        </template>

        <!-- Consumption confirmed badge -->
        <v-alert v-if="consumptionData" dense text type="success"
          class="mb-0 mt-2 py-1" style="font-size:0.8rem;">
          <v-icon left small>mdi-check-circle</v-icon>
          {{ consumptionData.totalConsumption | number }} kWh confirmed
          <span class="ml-2 grey--text">
            ({{ consumptionData.dataSource === 'automatic' ? 'Auto' : 'Manual' }}
            · {{ billingDurationDays }}d)
          </span>
          <v-btn x-small text color="grey" class="ml-2" @click="clearConsumption">
            change
          </v-btn>
        </v-alert>

        <!-- Error -->
        <v-alert v-if="consumptionError" dense type="error"
          dismissible class="mt-2" @input="consumptionError = null">
          {{ consumptionError }}
        </v-alert>

        <!-- ③ Bill inputs — shown once consumption is confirmed -->
        <template v-if="consumptionData">
          <v-divider class="my-4" />
          <div class="field-section-label">Bill Details</div>
          <v-row dense>
            <v-col cols="12" sm="6" v-if="showSingleKvaDemandInput">
              <v-text-field
                v-model.number="manualData.kvaDemand"
                label="kVA Demand"
                type="number" step="0.01" min="0"
                suffix="kVA" dense outlined hide-details
                @input="onBillFieldInput"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="manualData.actualBillAmount"
                label="Actual Bill Amount"
                type="number" step="0.01" min="0"
                :suffix="$currencySymbol" dense outlined hide-details
                @input="onBillFieldInput"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="manualData.actualKwhFromBill"
                label="Actual kWh from Bill"
                type="number" step="0.1" min="0"
                suffix="kWh" dense outlined hide-details
                hint="kWh shown on physical bill" persistent-hint
                @input="onBillFieldInput"
              />
            </v-col>
            <v-col cols="12" sm="6" v-if="showManualKvaDemandCharges">
              <v-text-field
                v-model.number="manualData.kvaDemandCharges"
                label="kVA Demand Charges"
                type="number" step="0.01" min="0"
                :suffix="$currencySymbol" dense outlined hide-details
                @input="onBillFieldInput"
              />
            </v-col>
          </v-row>

          <!-- TOU demand split -->
          <template v-if="showTimeOfUseDemandSplit">
            <div class="field-section-label mt-4">Maximum Demand</div>
            <v-simple-table dense class="tou-table mb-1">
              <template #default>
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>kW</th>
                    <th class="text-right">Charge</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="line in touDemandLines" :key="line.key">
                    <td class="text-caption font-weight-medium">
                      {{ line.label }}
                      <span class="grey--text ml-1">@{{ formatRate(line.rate) }}</span>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="manualData.timeOfUseDemand[line.key].kw"
                        type="number" step="0.01" min="0"
                        dense outlined hide-details class="tou-input"
                        @input="onBillFieldInput"
                      />
                    </td>
                    <td class="text-right text-caption font-weight-bold">
                      {{ formatDemandCharge(line.key) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div class="text-caption grey--text text-right mb-2">
              Total demand charges:
              <strong>{{ formattedTouDemandTotal }} {{ $currencySymbol }}</strong>
            </div>
          </template>

          <!-- Notes -->
          <v-textarea
            v-model="manualData.notes"
            label="Notes (optional)"
            rows="2" dense outlined hide-details class="mt-3"
            @input="onBillFieldInput"
          />
        </template>

      </v-form>

      <!-- Action bar -->
      <v-divider />
      <div class="d-flex justify-end pa-3">
        <v-btn color="primary"
          :disabled="!canProceedToReview"
          :loading="calculating"
          @click="proceedToReview">
          Calculate &amp; Review
          <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- ── STEP 2 ─────────────────────────────────────────────────────── -->
    <div v-show="currentStep === 2">
      <!-- Calculating -->
      <div v-if="calculating" class="text-center pa-8">
        <v-progress-circular indeterminate color="primary" size="48" />
        <div class="text-body-2 mt-3 grey--text">Calculating bill…</div>
      </div>

      <template v-else-if="calculatedBill && finalBillData">
        <BillBreakdownDisplay :bill-data="calculatedBill" use-current-rates />
        <BillSaveComponent
          class="mt-0"
          :bill-data="finalBillData"
          @record-saved="onRecordSaved"
          @show-message="showMessage"
        />
      </template>

      <v-divider />
      <div class="pa-3">
        <v-btn text small @click="goBack">
          <v-icon left small>mdi-arrow-left</v-icon>Back
        </v-btn>
      </div>
    </div>

    <!-- Hidden child components — kept for overlap detection + rate loading -->
    <div style="display:none">
      <BillingPeriodSelector
        ref="periodSelectorLogic"
        @consumption-data-retrieved="onConsumptionDataRetrieved"
        @consumption-data-cleared="onConsumptionDataCleared"
        @show-message="showMessage"
      />
      <ManualDataEntryForm
        ref="manualDataFormLogic"
        :billing-period="billingPeriod"
        :consumption-data="consumptionData"
        :show-validation-status="false"
        @form-submitted="onManualDataSubmitted"
        @form-data-changed="onManualDataChanged"
        @show-message="showMessage"
      />
    </div>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color"
      :timeout="snackbar.timeout" top>
      {{ snackbar.message }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BillingPeriodSelector from '../components/billing/BillingPeriodSelector.vue';
import ManualDataEntryForm from '../components/billing/ManualDataEntryForm.vue';
import BillBreakdownDisplay from '../components/billing/BillBreakdownDisplay.vue';
import BillSaveComponent from '../components/billing/BillSaveComponent.vue';
import costCategoryMixin from '../mixins/costCategoryMixin';
import { resolveBillingMode, isTimeOfUseBillingMode } from '../utils/billingMode';
import {
  buildTimeOfUseDemandLines,
  buildTimeOfUseUnitLines,
  getActiveChargeCodes,
} from '../utils/chargeMaster';
import {
  calculateTimeOfUseBillingRate,
  calculateTimeOfUseTotalCalculated,
} from '../utils/timeOfUseBilling';
import appConfig from '../config/appConfig';

const MANUAL_KVA_DEMAND_CHARGE_CODES = ['KVA_DEMAND_CHARGES', 'MANUAL_KVA_DEMAND_CHARGES'];

export default {
  name: 'BillingEntry',
  mixins: [costCategoryMixin],

  components: {
    BillingPeriodSelector,
    ManualDataEntryForm,
    BillBreakdownDisplay,
    BillSaveComponent,
  },

  filters: {
    number(val) {
      return (parseFloat(val) || 0).toLocaleString();
    },
  },

  data() {
    return {
      currentStep: 1,
      // period pickers
      period: { startDate: null, endDate: null },
      startDateMenu: false,
      endDateMenu: false,
      // consumption
      dataEntryMode: 'automatic',
      loadingConsumption: false,
      consumptionError: null,
      consumptionData: null,
      billingPeriod: null,
      // manual standard readings
      manualReading: { initial: 0, final: 0 },
      // manual TOU readings (keyed by line.key)
      touReadings: {
        onPeak: { initial: 0, final: 0, units: 0 },
        offPeak: { initial: 0, final: 0, units: 0 },
        sunday: { initial: 0, final: 0, units: 0 },
        misc: { initial: 0, final: 0, units: 0 },
      },
      // bill fields
      manualData: {
        kvaDemand: 0,
        actualBillAmount: 0,
        kvaDemandCharges: 0,
        actualKwhFromBill: 0,
        timeOfUseUnits: null,
        timeOfUseDemand: null,
        oilSurchargeRate: 0,
        notes: '',
      },
      calculating: false,
      calculatedBill: null,
      finalBillData: null,
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 5000,
      },
    };
  },

  created() {
    this.fetchRates().catch((error) => {
      console.error('Error loading billing rates:', error);
    });
  },

  computed: {
    ...mapGetters('rateMaster', ['allRates']),

    billingMode() {
      return resolveBillingMode({
        activeCategories: this.costCategories,
        consumptionData: this.consumptionData,
        manualData: this.manualData,
      });
    },
    isTimeOfUseMode() { return isTimeOfUseBillingMode(this.billingMode); },
    shouldUseUnitCostForBilling() {
      return appConfig.getBillingCapabilities().useTouUnitCost === true
        && (
          this.isTimeOfUseMode
          || this.calculatedBill?.calculationMode === 'TIME_OF_USE'
        );
    },
    usesCalculatedDemandCharge() {
      const cc = this.calculatedBill?.calculatedCharges || {};
      return Object.prototype.hasOwnProperty.call(cc, 'KVA_DEMAND_CHARGE')
        || Object.prototype.hasOwnProperty.call(cc, 'KVA_PER_UNIT_CHARGE');
    },
    shouldSplitDemandByTouPeriod() {
      return appConfig.getBillingCapabilities().splitDemandByTouPeriod === true;
    },
    // Date formatting
    formattedStartDate() {
      return this.period.startDate ? this.$formatDate(this.period.startDate) : '';
    },
    formattedEndDate() {
      return this.period.endDate ? this.$formatDate(this.period.endDate) : '';
    },
    billingDurationDays() {
      if (!this.period.startDate || !this.period.endDate) return 0;
      const diff = Math.abs(new Date(this.period.endDate) - new Date(this.period.startDate));
      return Math.floor(diff / 86400000) + 1;
    },
    // TOU unit lines from cost categories
    touUnitLines() {
      const lines = buildTimeOfUseUnitLines(this.costCategories);
      this.ensureTouReadings(lines);
      return lines;
    },
    // TOU demand lines
    touDemandLines() {
      const lines = buildTimeOfUseDemandLines(this.costCategories);
      return lines.map((line) => ({ ...line, rate: this.getTouDemandRate(line) }));
    },
    // TOU consumption totals
    touTotalUnits() {
      return Object.values(this.touReadings)
        .reduce((sum, r) => sum + (parseFloat(r.units) || 0), 0);
    },
    formattedTouTotal() {
      return this.touTotalUnits.toLocaleString('en-US', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      });
    },
    // Standard consumption
    calculatedConsumption() {
      const i = parseFloat(this.manualReading.initial) || 0;
      const f = parseFloat(this.manualReading.final) || 0;
      return f - i;
    },
    // Bill field visibility
    showSingleKvaDemandInput() {
      return !this.isTimeOfUseMode || !this.shouldSplitDemandByTouPeriod;
    },
    showManualKvaDemandCharges() {
      const codes = getActiveChargeCodes(this.costCategories);
      return !this.isTimeOfUseMode
        && codes.some((c) => MANUAL_KVA_DEMAND_CHARGE_CODES.includes(c));
    },
    showTimeOfUseDemandSplit() {
      return this.isTimeOfUseMode && this.shouldSplitDemandByTouPeriod;
    },
    // TOU demand totals
    touDemandChargeTotal() {
      if (!this.manualData.timeOfUseDemand) return 0;
      return Object.values(this.manualData.timeOfUseDemand).reduce((sum, line) => {
        const kw = Math.max(parseFloat(line?.kw) || 0, 0);
        const rate = parseFloat(line?.rate) || 0;
        return sum + kw * rate;
      }, 0);
    },
    formattedTouDemandTotal() {
      return this.touDemandChargeTotal.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    // Effective demand for calculation
    effectiveKvaDemand() {
      if (this.isTimeOfUseMode && this.shouldSplitDemandByTouPeriod) {
        return Object.values(this.manualData.timeOfUseDemand || {})
          .reduce((sum, line) => sum + (parseFloat(line?.kw) || 0), 0);
      }
      return parseFloat(this.manualData.kvaDemand) || 0;
    },
    timeOfUseTotalUnits() {
      const tou = this.manualData.timeOfUseUnits
        || this.getTimeOfUseUnitsFrom(this.consumptionData) || {};
      return Object.values(tou).reduce((sum, line) => (
        sum + (parseFloat(line?.units) || parseFloat(line?.difference) || 0)
      ), 0);
    },
    canProceedToReview() {
      if (!this.consumptionData || !this.billingPeriod) return false;
      return this.manualData.actualBillAmount > 0 && this.manualData.kvaDemand >= 0;
    },
  },

  methods: {
    ...mapActions('billing', {
      calculateBillAction: 'calculateBill',
    }),
    ...mapActions('rateMaster', ['fetchRates']),

    getTimeOfUseUnitsFrom(data = {}) {
      return data?.timeOfUseUnits || data?.touBreakdown || {};
    },
    getTimeOfUseDemandFrom(data = {}) {
      return data?.timeOfUseDemand || data?.demandBreakdown || {};
    },

    // ── Inline period/consumption logic ──────────────────────────────────

    onDataEntryModeChange() {
      this.clearConsumption();
      if (this.dataEntryMode === 'automatic') {
        this.manualReading = { initial: 0, final: 0 };
        this.resetTouReadings();
      }
    },

    syncManualConsumption() { /* triggers calculatedConsumption reactive */ },

    ensureTouReadings(lines = []) {
      lines.forEach((line) => {
        if (!this.touReadings[line.key]) {
          this.$set(this.touReadings, line.key, { initial: 0, final: 0, units: 0 });
        }
      });
    },

    syncTouLine(key) {
      const r = this.touReadings[key];
      const f = parseFloat(r.final) || 0;
      const i = parseFloat(r.initial) || 0;
      this.$set(this.touReadings, key, { ...r, units: f - i });
    },

    formatTouUnits(key) {
      const u = parseFloat(this.touReadings[key]?.units) || 0;
      return u.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    resetTouReadings() {
      this.touUnitLines.forEach((line) => {
        this.$set(this.touReadings, line.key, { initial: 0, final: 0, units: 0 });
      });
    },

    getTouDemandRate(line = {}) {
      const effectiveDate = this.period.endDate || this.billingPeriod?.endDate;
      if (!line.categoryCode || !effectiveDate) return 0;
      const date = new Date(effectiveDate).getTime();
      const match = (this.allRates || [])
        .filter((r) => {
          const cc = r.costCategory || r.cost_category;
          const active = r.isActive !== false && r.is_active !== false;
          const from = new Date(r.effectiveFrom || r.effective_from).getTime();
          const to = new Date(r.effectiveTo || r.effective_to).getTime();
          return active && cc === line.categoryCode && from <= date && to >= date;
        })
        .sort((a, b) => new Date(b.effectiveFrom || b.effective_from)
          - new Date(a.effectiveFrom || a.effective_from))[0];
      return match ? (parseFloat(match.rate) || 0) : 0;
    },

    formatRate(rate) {
      return (parseFloat(rate) || 0).toLocaleString('en-US', {
        minimumFractionDigits: 4, maximumFractionDigits: 4,
      });
    },

    formatDemandCharge(key) {
      const line = this.manualData.timeOfUseDemand?.[key] || {};
      const kw = Math.max(parseFloat(line.kw) || 0, 0);
      const rate = parseFloat(line.rate) || 0;
      return (kw * rate).toLocaleString('en-US', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      });
    },

    onBillFieldInput() { /* reactive — no action needed, template reads manualData */ },

    clearConsumption() {
      this.consumptionData = null;
      this.billingPeriod = null;
      this.calculatedBill = null;
      this.finalBillData = null;
      this.manualData.timeOfUseUnits = null;
      this.manualData.timeOfUseDemand = null;
    },

    async fetchConsumptionData() {
      if (!this.period.startDate || !this.period.endDate) return;
      this.consumptionError = null;
      try {
        const startDate = new Date(this.period.startDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(this.period.endDate);
        endDate.setHours(23, 59, 59, 999);

        // delegate to hidden BillingPeriodSelector for overlap check + fetch
        if (this.$refs.periodSelectorLogic) {
          this.$refs.periodSelectorLogic.billingPeriod = {
            startDate: this.period.startDate,
            endDate: this.period.endDate,
          };
          await this.$refs.periodSelectorLogic.fetchAutomaticConsumptionData();
        }
      } catch (err) {
        this.consumptionError = err.message || 'Failed to fetch consumption data';
      } finally {
        this.loadingConsumption = false;
      }
    },

    confirmManualData() {
      const billingPeriod = { ...this.period };
      const existingDemand = this.manualData.timeOfUseDemand || {};
      let consumptionData;

      if (this.isTimeOfUseMode) {
        const touUnits = {};
        this.touUnitLines.forEach((line) => {
          const r = this.touReadings[line.key] || {};
          touUnits[line.key] = {
            categoryCode: line.categoryCode,
            label: line.label,
            initialReading: parseFloat(r.initial) || 0,
            finalReading: parseFloat(r.final) || 0,
            units: parseFloat(r.units) || 0,
            difference: parseFloat(r.units) || 0,
            rate: line.rate || 0,
          };
        });
        consumptionData = {
          totalConsumption: this.touTotalUnits,
          initialReading: null,
          finalReading: null,
          billingPeriod,
          dataSource: 'manual',
          hasActualReadings: false,
          timeOfUseUnits: touUnits,
          touBreakdown: touUnits,
          demandBreakdown: {},
        };
      } else {
        consumptionData = {
          totalConsumption: this.calculatedConsumption,
          initialReading: parseFloat(this.manualReading.initial) || 0,
          finalReading: parseFloat(this.manualReading.final) || 0,
          billingPeriod,
          dataSource: 'manual',
          hasActualReadings: true,
        };
      }

      this.consumptionData = consumptionData;
      this.billingPeriod = billingPeriod;
      this.manualData.timeOfUseUnits = this.getTimeOfUseUnitsFrom(consumptionData);
      this.manualData.timeOfUseDemand = this.getTimeOfUseDemandFrom(consumptionData);

      if (this.showTimeOfUseDemandSplit) {
        const demand = {};
        this.touDemandLines.forEach((line) => {
          const existingLine = existingDemand[line.key] || {};
          demand[line.key] = {
            ...existingLine,
            categoryCode: line.categoryCode,
            label: line.label,
            kw: parseFloat(existingLine.kw) || 0,
            rate: line.rate || existingLine.rate || 0,
          };
        });
        this.manualData.timeOfUseDemand = demand;
      }
    },

    // Events from hidden child components
    onConsumptionDataRetrieved(data) {
      this.consumptionData = data.consumptionData;
      this.billingPeriod = data.billingPeriod || this.period;
      this.manualData.timeOfUseUnits = this.getTimeOfUseUnitsFrom(data.consumptionData);
      this.manualData.timeOfUseDemand = this.getTimeOfUseDemandFrom(data.consumptionData);
    },
    onConsumptionDataCleared() { this.clearConsumption(); },
    onManualDataChanged() {},
    onManualDataSubmitted() {},

    async proceedToReview() {
      await this.runCalculation();
      if (this.calculatedBill) {
        this.buildFinalBillData();
        this.currentStep = 2;
      }
    },

    async runCalculation() {
      if (!this.consumptionData) {
        this.showMessage({ type: 'error', message: 'Missing consumption data for calculation' });
        return;
      }

      this.calculating = true;

      try {
        const result = await this.calculateBillAction({
          consumptionData: this.consumptionData,
          kvaDemand: this.effectiveKvaDemand,
          billingDate: new Date(this.billingPeriod.endDate),
          billingMode: this.billingMode,
          tariffMode: this.isTimeOfUseMode ? 'TIME_OF_USE' : 'STANDARD',
          tariffData: this.buildTariffData(),
          activeCategories: this.costCategories,
          rates: this.allRates,
        });

        const consumptionDataForSave = this.buildConsumptionDataForSave();
        const unitCostForBilling = this.calculateUnitCostForBilling();
        const totalConsumption = consumptionDataForSave?.totalConsumption || 0;
        const totalCalculated = this.calculateDisplayedTotal(
          unitCostForBilling,
          totalConsumption,
          result.calculatedCharges.totalCalculated,
        );

        this.calculatedBill = {
          ...result,
          calculationMode: this.isTimeOfUseMode ? 'TIME_OF_USE' : result.calculationMode,
          consumptionData: consumptionDataForSave,
          timeOfUseUnits: this.manualData.timeOfUseUnits
            || consumptionDataForSave?.touBreakdown || {},
          timeOfUseDemand: this.manualData.timeOfUseDemand
            || consumptionDataForSave?.demandBreakdown || {},
          oilSurchargeRate: this.manualData.oilSurchargeRate || 0,
          unitCostForBilling,
          actualBillAmount: this.manualData.actualBillAmount,
          variance: this.manualData.actualBillAmount - totalCalculated,
        };
      } catch (error) {
        console.error('Error calculating bill:', error);
        this.showMessage({ type: 'error', message: error.message || 'Failed to calculate bill' });
      } finally {
        this.calculating = false;
      }
    },

    buildFinalBillData() {
      const timeOfUseUnits = this.manualData.timeOfUseUnits
        || this.getTimeOfUseUnitsFrom(this.consumptionData) || null;
      const timeOfUseDemand = this.manualData.timeOfUseDemand
        || this.getTimeOfUseDemandFrom(this.consumptionData) || null;
      const unitCostForBilling = this.calculateUnitCostForBilling();
      const calculatedCharges = this.calculatedBill.calculatedCharges || {};
      const totalConsumption = this.buildConsumptionDataForSave()?.totalConsumption || 0;
      const totalCalculated = this.calculateDisplayedTotal(
        unitCostForBilling,
        totalConsumption,
        calculatedCharges.totalCalculated,
      );

      this.finalBillData = {
        billingPeriod: this.billingPeriod,
        consumptionData: this.buildConsumptionDataForSave(),
        kvaDemand: this.effectiveKvaDemand,
        calculatedCharges,
        actualBillAmount: this.manualData.actualBillAmount,
        kvaDemandCharges: this.usesCalculatedDemandCharge
          ? 0 : this.manualData.kvaDemandCharges,
        actualKwhFromBill: this.manualData.actualKwhFromBill,
        timeOfUseUnits,
        timeOfUseDemand,
        oilSurchargeRate: this.manualData.oilSurchargeRate,
        serviceCharge: calculatedCharges.SERVICE_CHARGE || calculatedCharges.serviceCharges || 0,
        unitCostForBilling,
        calculationMode: this.isTimeOfUseMode ? 'TIME_OF_USE' : this.billingMode,
        variance: this.manualData.actualBillAmount - totalCalculated,
        ratesUsed: this.calculatedBill.ratesUsed,
        notes: this.manualData.notes || '',
        createdBy: appConfig.getParentUserName(),
      };
    },

    onRecordSaved(savedRecord) {
      this.showMessage({
        type: 'success',
        message: `Billing record saved successfully with ID: ${savedRecord.id}`,
      });
      setTimeout(() => {
        this.$router.push('/billing-history');
      }, 2000);
    },

    goBack() {
      this.currentStep = 1;
    },

    resetForm() {
      this.currentStep = 1;
      this.period = { startDate: null, endDate: null };
      this.dataEntryMode = 'automatic';
      this.manualReading = { initial: 0, final: 0 };
      this.resetTouReadings();
      this.clearConsumption();
      this.manualData = {
        kvaDemand: 0,
        actualBillAmount: 0,
        kvaDemandCharges: 0,
        actualKwhFromBill: 0,
        timeOfUseUnits: null,
        timeOfUseDemand: null,
        oilSurchargeRate: 0,
        notes: '',
      };
      this.showMessage({ type: 'info', message: 'Form has been reset' });
    },

    showMessage({ type, message }) {
      this.snackbar = {
        show: true,
        message,
        color: this.getSnackbarColor(type),
        timeout: type === 'error' ? 8000 : 5000,
      };
    },

    getSnackbarColor(type) {
      const map = {
        error: 'error', warning: 'warning', info: 'info',
      };
      return map[type] || 'success';
    },

    buildTariffData() {
      if (!this.isTimeOfUseMode) return null;
      return {
        unitLines: this.manualData.timeOfUseUnits
          || this.getTimeOfUseUnitsFrom(this.consumptionData) || {},
        demandLines: this.getDemandLinesForCalculation(),
        oilSurchargeRate: this.manualData.oilSurchargeRate || 0,
      };
    },

    calculateUnitCostForBilling() {
      if (!this.shouldUseUnitCostForBilling) return 0;
      return calculateTimeOfUseBillingRate(this.calculatedBill || {
        subtotal: this.calculatedBill?.subtotal,
        calculatedCharges: this.calculatedBill?.calculatedCharges,
        consumptionData: this.consumptionData,
      });
    },

    calculateDisplayedTotal(unitCostForBilling, consumption, fallbackTotal = 0) {
      if (this.shouldUseUnitCostForBilling && this.calculatedBill) {
        return calculateTimeOfUseTotalCalculated(this.calculatedBill);
      }
      const rate = parseFloat(unitCostForBilling) || 0;
      const units = parseFloat(consumption) || 0;
      if (rate > 0 && units > 0) return rate * units;
      return parseFloat(fallbackTotal) || 0;
    },

    buildConsumptionDataForSave() {
      if (!this.isTimeOfUseMode) return this.consumptionData;
      const timeOfUseUnits = this.manualData.timeOfUseUnits
        || this.getTimeOfUseUnitsFrom(this.consumptionData) || {};
      const timeOfUseDemand = this.shouldSplitDemandByTouPeriod
        ? (this.manualData.timeOfUseDemand
          || this.getTimeOfUseDemandFrom(this.consumptionData) || {})
        : {};
      const totalConsumption = this.timeOfUseTotalUnits
        || this.consumptionData?.totalConsumption || 0;
      return {
        ...this.consumptionData,
        totalConsumption,
        timeOfUseUnits,
        timeOfUseDemand,
        touBreakdown: timeOfUseUnits,
        demandBreakdown: timeOfUseDemand,
      };
    },

    getDemandLinesForCalculation() {
      if (this.shouldSplitDemandByTouPeriod) {
        return this.manualData.timeOfUseDemand
          || this.getTimeOfUseDemandFrom(this.consumptionData) || {};
      }
      const kvaDemand = parseFloat(this.manualData.kvaDemand) || 0;
      return kvaDemand > 0
        ? {}
        : {};
    },
  },
};
</script>

<style scoped>
.field-section-label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0,0,0,0.45);
  margin-bottom: 6px;
}

.tou-table {
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 4px;
}

.tou-input {
  max-width: 120px;
  margin: 2px auto;
}
</style>
